import { createContext, useContext, ReactNode } from 'react';
import { useDashboardWithPolling } from '@/hooks/useDashboardWithPolling';
import { useAuth } from '@/hooks/useAuth';
import { CampaignStatus } from '@/types/states';

// Types
interface DashboardCampaign {
  id: string;
  title: string;
  description: string;
  candidate_name: string;
  candidate_email: string;
  target_position: string;
  status: CampaignStatus;
  is_draft?: boolean | null;
  organization_id: string;
  created_at: string;
  updated_at: string;
  evaluation_criteria: any[];
  last_activity: string | null;
  unique_link: string | null;
  ai_extracted_data: any;
  referents_count: number;
  completed_referents: number;
  referents_requested_count?: number;
  refused_referents?: number;
}

interface ActivityNotification {
  id: string;
  type: string;
  message: string;
  is_read: boolean;
  created_at: string | null;
  campaign_id: string | null;
  referent_id: string | null;
}

interface DashboardStats {
  credits_remaining: number;
  refused_campaigns: number;
  pending_candidate_campaigns: number;
  pending_referents_campaigns: number;
  completed_campaigns: number;
}

interface DashboardDataContextValue {
  stats: DashboardStats | null;
  campaigns: DashboardCampaign[];
  notifications: ActivityNotification[];
  unreadCount: number;
  campaignsLoading: boolean;
  campaignsFetched: boolean; // ✅ Ajouté pour gate l'empty state
  notificationsLoading: boolean;
  refetchCampaigns: () => void;
  refetchNotifications: () => void;
  markNotificationAsRead: (notificationId: string) => Promise<void>;
  markAllNotificationsAsRead: () => Promise<void>;
  isRealtimeConnected: boolean;
}

// Context
const DashboardDataContext = createContext<DashboardDataContextValue | undefined>(undefined);

// Provider Props
interface DashboardDataProviderProps {
  children: ReactNode;
}

/**
 * ✅ PHASE 1B : Provider centralisé pour les données Dashboard
 * 
 * Objectif : 1 seul appel à useDashboardWithPolling → 1 seul useSecureRealtime par table
 * 
 * Avantages :
 * - Centralise les side-effects Realtime
 * - Évite les multiples instances dans les composants enfants
 * - Attend que userId + orgId + userRole soient définis avant de rendre (évite remount)
 * 
 * Gating isReady :
 * - Empêche subscribe/cleanup pendant un état incomplet
 * - Garantit 1 seule init "propre" (pas de queryKey qui change)
 * - Résultat attendu : 2 subs max en dev StrictMode, 1 en prod
 */
export const DashboardDataProvider = ({ children }: DashboardDataProviderProps) => {
  const { user, profile } = useAuth();
  
  // ✅ Gating complet : attendre userId + orgId + userRole
  const userId = user?.id;
  const orgId = profile?.organization_id;
  const userRole = profile?.role;
  const isReady = !!userId && !!orgId && !!userRole;

  // ✅ Pendant le chargement, afficher un loader stable (pas de side-effects)
  if (!isReady) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // ✅ Une fois isReady = true, appeler useDashboardWithPolling (1 seule fois)
  // À ce stade, userId/orgId/userRole sont stables → pas de remount
  return <DashboardDataProviderReady>{children}</DashboardDataProviderReady>;
};

/**
 * Composant interne qui appelle useDashboardWithPolling une fois isReady = true
 */
const DashboardDataProviderReady = ({ children }: { children: ReactNode }) => {
  // ✅ 1 seul appel à useDashboardWithPolling (qui contient useSecureRealtime)
  const dashboardData = useDashboardWithPolling();

  return (
    <DashboardDataContext.Provider value={dashboardData}>
      {children}
    </DashboardDataContext.Provider>
  );
};

/**
 * Hook pour consommer les données Dashboard depuis n'importe quel composant enfant
 * 
 * Usage :
 * ```tsx
 * const { campaigns, refetchCampaigns, notifications, unreadCount } = useDashboardData();
 * ```
 */
export const useDashboardData = (): DashboardDataContextValue => {
  const context = useContext(DashboardDataContext);
  
  if (context === undefined) {
    throw new Error('useDashboardData must be used within a DashboardDataProvider');
  }
  
  return context;
};
